// frontend/src/pages/VerifyEmailPage.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Email } from "@mui/icons-material";
import { motion } from "framer-motion";
import api from "../services/api";
import { checkAuth } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store/store";
import { handlePasteCode } from "../utils/handlePasteCode";
import { getStoredPlan } from "../utils/planStorage";
import { StepIndicator } from "../components/auth";
import { useRegistrationFlowData } from "../utils/registrationFlow";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  // Get dynamic step configuration based on flow type
  const { totalSteps, currentStepLabel } =
    useRegistrationFlowData("verify_email");

  useEffect(() => {
    // Get email from session storage
    const storedEmail = sessionStorage.getItem("registration_email");
    if (!storedEmail) {
      navigate("/register");
    } else {
      setEmail(storedEmail);
    }
  }, [navigate]);

  useEffect(() => {
    // Handle resend timer countdown
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = verificationCode.join("");
    if (code.length !== 6) {
      setError("Please enter the complete verification code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Check if user has a pre-selected plan
      const storedPlan = getStoredPlan();

      // Pass skipTrialSetup=true if user is in paid flow (has pre-selected plan)
      const response = await api.post("/auth/verify-email-code", {
        email,
        code,
        skipTrialSetup: !!storedPlan, // Skip trial setup if going to paid flow
      });
      sessionStorage.removeItem("registration_email");

      // NEW FLOW:
      // - If user has pre-selected plan: go to subscription page (paid flow)
      // - If no plan selected: go directly to dashboard (free trial flow)
      if (storedPlan) {
        // Paid flow: redirect to subscription checkout
        window.location.href = `/subscription?plan=${storedPlan}`;
      } else {
        // Free trial flow: Fire signup_free_trial event and redirect to required booking step
        window.dataLayer = window.dataLayer || [];

        const trialEventData = {
          event: "signup_free_trial",
          value: 0,
          currency: "JPY",
          is_trial: true,
          transaction_id: null,
          user_id: response.data?.user?.id || null,
          is_subscription_paid: false,
          subscription_status: "trial",
          ecommerce: {
            transaction_id: null,
            value: 0,
            currency: "JPY",
            items: [
              {
                item_id: "trial_free",
                item_name: "Free Trial (No Credit Card)",
                item_category: "Subscription",
                price: 0,
                quantity: 1,
              },
            ],
          },
        };

        console.log("📊 DataLayer Push (Free Trial - No CC):", trialEventData);
        console.table(trialEventData);
        window.dataLayer.push(trialEventData);

        window.location.href = "/calendar";
      }

      await dispatch(checkAuth());
    } catch (err: any) {
      setError(
        err.response?.data?.error.message ||
          err.response?.data?.error ||
          "Invalid verification code",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setResendLoading(true);
    setError("");

    try {
      await api.post("/auth/resend-verification", { email });

      setResendTimer(60); // Set 60 second cooldown
      setError(""); // Clear any errors

      // Show success message
      const successMessage = document.createElement("div");
      successMessage.style.cssText =
        'position: fixed; top: 20px; right: 20px; background: #483C32; color: white; padding: 16px 24px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 9999; font-family: "Inter", sans-serif;';
      successMessage.textContent = "Verification code resent successfully!";
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.error.message ||
          err.response?.data?.error ||
          "Failed to resend verification code",
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: "linear-gradient(135deg, #A6531C 0%, #7ED4D0 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -300,
          left: -300,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.05)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{ borderRadius: 3, boxShadow: 10, my: 4 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
              <Box textAlign="center" m={4}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #A6531C 0%, #7ED4D0 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    <Email sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </motion.div>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="primary"
                  gutterBottom
                >
                  Verify Your Email
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We've sent a verification code to
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {email}
                </Typography>
              </Box>

              {/* Progress Indicator - Dynamic based on flow type */}
              <StepIndicator
                currentStep={2}
                label={currentStepLabel}
                totalSteps={totalSteps}
              />

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mb={2}
                >
                  Enter the 6-digit code
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  {verificationCode.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={(e) =>
                        handlePasteCode(
                          e,
                          verificationCode,
                          setVerificationCode,
                          inputRefs,
                        )
                      }
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: "center",
                          fontSize: "1.5rem",
                          fontWeight: 600,
                        },
                      }}
                      sx={{
                        width: 50,
                        "& .MuiOutlinedInput-root": {
                          height: 60,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Stack spacing={2} mt={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerify}
                  disabled={verificationCode.join("").length !== 6 || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>

                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Didn't receive the code?{" "}
                    <Button
                      variant="text"
                      onClick={handleResend}
                      disabled={resendTimer > 0 || resendLoading}
                      sx={{ textTransform: "none" }}
                    >
                      {resendLoading ? (
                        <CircularProgress size={16} />
                      ) : resendTimer > 0 ? (
                        `Resend in ${resendTimer}s`
                      ) : (
                        "Resend Code"
                      )}
                    </Button>
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};
